/**
 * Global Error Boundary — v9.9.3
 *
 * Catches uncaught React errors and reports them to Sentry with full context.
 * Displays error + component stack in dev for easier debugging.
 */
import { Component, type ReactNode } from "react";
import { AlertTriangle } from "lucide-react";
import { captureError } from "@/lib/sentry";

interface State {
  error: Error | null;
  errorInfo: any;
  showDetails: boolean;
}

interface Props {
  children: ReactNode;
  route?: string;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, errorInfo: null, showDetails: false };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    this.setState({ errorInfo });
    captureError(error, {
      route: this.props.route || (typeof window !== "undefined" ? window.location.pathname : ""),
      componentStack: errorInfo.componentStack?.slice(0, 2000),
      reactError: true,
    });
  }

  render() {
    if (this.state.error) {
      const { error, errorInfo, showDetails } = this.state;
      return (
        <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center p-4">
          <div className="max-w-2xl w-full text-center">
            <AlertTriangle className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2">Erreur d'affichage</h1>
            <p className="text-sm text-slate-400 mb-2">Cette route a rencontré une erreur. Elle a été reportée automatiquement.</p>
            <code className="text-xs text-red-300 block bg-slate-900 p-2 rounded mb-4 text-left">
              {error.message}
            </code>
            <div className="flex items-center justify-center gap-2">
              <button
                onClick={() => { this.setState({ error: null, errorInfo: null, showDetails: false }); window.location.reload(); }}
                className="px-4 py-2 bg-sky-500 hover:bg-sky-600 rounded text-sm"
              >
                Recharger
              </button>
              <button
                onClick={() => this.setState({ showDetails: !showDetails })}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded text-sm"
              >
                {showDetails ? "Masquer" : "Détails"} techniques
              </button>
            </div>
            {showDetails && errorInfo?.componentStack && (
              <pre className="mt-4 text-left text-[10px] font-mono text-slate-400 bg-slate-900 p-3 rounded max-h-96 overflow-auto">
                {errorInfo.componentStack}
              </pre>
            )}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
